import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, paletteColors, Typography } from '@evaneos/b2b-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import { DossierId } from '../../resources/dossier/types';
import { fetchScheduledMessagesRequest, getDraftMessageRequest } from '../../resources/discussion';
import { useAppDispatch } from '../../shared/redux';
import {
    getDossierQueryKey,
    useDossierAndAgentRequestStatus,
    useGetDossierData,
    useHeatLevelLabelByDossierId,
} from '../../resources/dossier/queries';
import { ERROR, PENDING, SUCCESS } from '../../design-system/constants';
import { Loader } from '../../design-system/Loader';
import { getAgentsQueryKey } from '../../resources/agent/hooks';

import { ErrorOverlay } from '../Pipeline/components/Error';
import { useGetTrackedTripProperties, useTrackPageViewAmplitudeOnce } from '../../shared/analytics';

import { PhoneCallProvider } from '../../shared/providers/PhoneCallContext';

import { Discussion } from './Discussion';

import { DossierNavbar } from './DossierNavbar/DossierNavbar';
import { DossierMainInfo } from './DossierMainInfo/DossierMainInfo';
import { DossierSecondaryInfo } from './DossierSecondaryInfo/DossierSecondaryInfo';
import { InitialRequest } from './InitialRequest/InitialRequest';
import { MobileNavigation, TabType } from './MobileNavigation';
import { DossierAlertsDisplay } from './DossierAlertsDisplay/DossierAlertsDisplay';
import { SidePanelProvider, useSidePanel } from './SidePanel/SidePanelContext';
import { SidePanel, DRAWER_WIDTH } from './SidePanel/SidePanel';
import { DossierLayoutInfoProvider, useDossierLayoutInfo } from './DossierLayoutInfoContext';
import { Phoning } from './Phoning/Phoning';

const DossierInner = () => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const { dossierId } = useParams<{ dossierId: DossierId }>();
    const { isOpen } = useSidePanel();
    const { firstColumnRef } = useDossierLayoutInfo();

    const [mobileActiveTab, setMobileActiveTab] = useState<TabType>('information');

    const status = useDossierAndAgentRequestStatus(dossierId);
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);
    const { dossier } = useGetDossierData(dossierId) || {};
    const heatLevelLabel = useHeatLevelLabelByDossierId(dossierId);

    useEffect(() => {
        dispatch(fetchScheduledMessagesRequest(dossierId));
        dispatch(getDraftMessageRequest(dossierId));
    }, [dispatch, dossierId]);

    useTrackPageViewAmplitudeOnce(
        (ampli, defaultProperties) => {
            if (!dossier) return;

            ampli.viewedPageFile({
                ...defaultProperties,
                ...trackedTripProperties,
                file_tab: 'information',
                sale_potential_level: heatLevelLabel ?? 'none',
                nb_tag_file: dossier.tags?.length ?? 0,
                has_budget_filled: !!dossier.budget,
                has_tripform_budget_filled: !!dossier.project?.idealBudget,
            });
        },
        {
            dependencies: [dossier, heatLevelLabel],
            isReady: status === SUCCESS && !!dossier,
        },
    );

    if (status !== SUCCESS) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    backgroundColor: 'background.paper',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    gap: 2,
                }}
            >
                {status === PENDING ? (
                    <>
                        <Loader color={paletteColors.green[700]} />
                        <Typography variant="h2" color={paletteColors.green[700]}>
                            {formatMessage({ id: 'global.loading' })}
                        </Typography>
                    </>
                ) : null}
                {status === ERROR ? (
                    <ErrorOverlay
                        onRefresh={() => {
                            void queryClient.invalidateQueries({
                                queryKey: getAgentsQueryKey(),
                            });
                            void queryClient.invalidateQueries({
                                queryKey: getDossierQueryKey(dossierId),
                            });
                        }}
                    />
                ) : null}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
                position: 'relative',
            }}
        >
            <Box
                component="main"
                sx={{
                    display: 'flex',
                    flex: 1,
                    position: 'relative',
                    flexDirection: { xs: 'column', md: 'row' },
                    overflow: 'hidden',
                    backgroundColor: 'var(--color-beige)',
                }}
            >
                <Box
                    sx={(theme) => ({
                        display: 'flex',
                        flex: 1,
                        flexDirection: { xs: 'column', md: 'row' },
                        overflow: 'hidden',
                        mr: { lg: isOpen ? `${DRAWER_WIDTH}px` : 0 },
                        transition: theme.transitions.create('margin', {
                            duration: theme.transitions.duration.standard,
                            easing: isOpen
                                ? theme.transitions.easing.easeOut
                                : theme.transitions.easing.sharp,
                        }),
                    })}
                >
                    <Box
                        ref={firstColumnRef}
                        sx={{
                            display: {
                                xs: mobileActiveTab === 'information' ? 'flex' : 'none',
                                md: 'flex',
                            },
                            width: { xs: '100%', md: 'auto' },
                            flexDirection: 'column',
                            backgroundColor: 'background.paper',
                            borderRight: '1px solid',
                            borderColor: 'divider',
                            overflow: 'hidden',
                            flex: 1,
                            minWidth: { md: 250 },
                            maxWidth: { md: 600 },
                        }}
                    >
                        <Box
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                                backgroundColor: 'background.paper',
                                flexShrink: 0,
                                paddingTop: { xs: 2, md: 3 },
                                paddingBottom: 1,
                                paddingX: { xs: 2, md: 3 },
                            }}
                        >
                            <DossierNavbar />
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                overflowY: 'auto',
                                padding: { xs: 2, md: 3 },
                                paddingTop: { md: 2 },
                            }}
                        >
                            <DossierMainInfo />
                            <DossierSecondaryInfo />
                            <DossierAlertsDisplay />
                            <InitialRequest />
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: {
                                xs: mobileActiveTab === 'discussion' ? 'flex' : 'none',
                                md: 'flex',
                            },
                            width: { xs: '100%', md: 'auto' },
                            flexDirection: 'column',
                            overflow: 'hidden',
                            flex: '1.5 4 0',
                        }}
                    >
                        <Discussion isSidePanelOpen={isOpen} />
                    </Box>
                </Box>
                <SidePanel />
            </Box>
            <MobileNavigation activeTab={mobileActiveTab} onTabChange={setMobileActiveTab} />
        </Box>
    );
};

export const DossierPage = () => {
    return (
        <PhoneCallProvider>
            <SidePanelProvider>
                <DossierLayoutInfoProvider>
                    <DossierInner />
                </DossierLayoutInfoProvider>
            </SidePanelProvider>
            <Phoning />
        </PhoneCallProvider>
    );
};
